/**
 * 文字種・入力パターン・日付範囲の設定UI（#3357）。
 *
 * ItemEditModal から切り出した表示コンポーネント。state は親が持ち、ここは props で受け取る。
 * 親（ItemEditModal）が 1190 行あり、制約を3種類足すと更に膨らむため分離した
 * （CLAUDE.md「1ファイル300行以下を目安」）。
 */
import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import CustomTextfield from '../../form/CustomTextfield';
import CustomSelectField from '../../form/CustomSelectField';
import {
  dateRangeUnitOptions,
  itemCharTypeOptions,
  itemValidationHelperText,
  itemValidationLabel,
  type DateRangeUnit,
  type ItemCharType,
} from '../../../../constants/itemValidation';
import { gray60, gray80 } from '../../../../constants/colors';

/**
 * 文字種の「制約なし」を表す値（#3357）。
 *
 * 空文字にすると CustomSelectField がプレースホルダー扱いにして
 * 「選択してください」を表示してしまうため、実値を持たせる。
 * プルダウンの先頭に置き、選び直すことで制約を解除できるようにする
 * （プルダウンは一度選ぶと未選択に戻せないため、解除手段が必要）。
 * 保存時は親が undefined に正規化する。
 */
export const CHAR_TYPE_NONE = 'none';

type Props = {
  /** 文字種・入力パターンを表示するか（テキスト・テキストエリア） */
  showCharType: boolean;
  /** 日付範囲を表示するか（日付型） */
  showDateRange: boolean;
  disabled?: boolean;

  charType: ItemCharType | typeof CHAR_TYPE_NONE;
  onCharTypeChange: (value: ItemCharType | typeof CHAR_TYPE_NONE) => void;

  pattern: string;
  onPatternChange: (value: string) => void;
  /** 正規表現として不正なときのエラー文言（未入力・正しい場合は undefined） */
  patternError?: string;

  patternMessage: string;
  onPatternMessageChange: (value: string) => void;

  dateNoPast: boolean;
  onDateNoPastChange: (value: boolean) => void;
  dateNoFuture: boolean;
  onDateNoFutureChange: (value: boolean) => void;
  datePastLimit: string;
  onDatePastLimitChange: (value: string) => void;
  datePastUnit: DateRangeUnit;
  onDatePastUnitChange: (value: DateRangeUnit) => void;
  dateFutureLimit: string;
  onDateFutureLimitChange: (value: string) => void;
  dateFutureUnit: DateRangeUnit;
  onDateFutureUnitChange: (value: DateRangeUnit) => void;
};

/** チェックボックスの色。既存のバリデーション設定と揃える */
const CHECKBOX_COLOR = '#FF6B35';

const AdvancedValidationSettings: React.FC<Props> = ({
  showCharType,
  showDateRange,
  disabled,
  charType,
  onCharTypeChange,
  pattern,
  onPatternChange,
  patternError,
  patternMessage,
  onPatternMessageChange,
  dateNoPast,
  onDateNoPastChange,
  dateNoFuture,
  onDateNoFutureChange,
  datePastLimit,
  onDatePastLimitChange,
  datePastUnit,
  onDatePastUnitChange,
  dateFutureLimit,
  onDateFutureLimitChange,
  dateFutureUnit,
  onDateFutureUnitChange,
}) => {
  if (!showCharType && !showDateRange) return null;

  return (
    <>
      {showCharType && (
        <>
          <Box sx={{ marginTop: '8px' }}>
            <CustomSelectField
              labelText={itemValidationLabel.charType}
              value={charType}
              onChange={(e): void =>
                onCharTypeChange(e.target.value as ItemCharType | typeof CHAR_TYPE_NONE)
              }
              list={[
                { value: CHAR_TYPE_NONE, option: '制約なし' },
                ...itemCharTypeOptions.map(opt => ({ value: opt.value, option: opt.option })),
              ]}
              disabled={disabled}
              height="56px"
              width="100%"
              formMb="4px"
            />
            <Typography sx={{ fontSize: '12px', color: gray60, marginTop: '4px' }}>
              {/* 「半角のみ」は漢字・かなも弾くため、選んだときだけ注意を出す */}
              {charType === 'halfWidth'
                ? itemValidationHelperText.charTypeHalfWidth
                : itemValidationHelperText.charType}
            </Typography>
          </Box>

          <Box sx={{ marginTop: '12px' }}>
            <CustomTextfield
              labelText={itemValidationLabel.pattern}
              value={pattern}
              onChange={(e): void => onPatternChange(e.target.value)}
              inputWidth="100%"
              height="56px"
              placeholder="例: ^[0-9]{4}-[0-9]{4}$"
              disabled={disabled}
              error={patternError !== undefined}
              helperText={patternError}
            />
            {patternError === undefined && (
              <Typography sx={{ fontSize: '12px', color: gray60, marginTop: '4px' }}>
                {itemValidationHelperText.pattern}
              </Typography>
            )}
          </Box>

          {/* 正規表現は何を意図したものか他人に読めないため、設定者が文言を書けるようにする */}
          {pattern !== '' && (
            <Box sx={{ marginTop: '12px' }}>
              <CustomTextfield
                labelText={itemValidationLabel.patternMessage}
                value={patternMessage}
                onChange={(e): void => onPatternMessageChange(e.target.value)}
                inputWidth="100%"
                height="56px"
                placeholder="例: ハイフン区切りの伝票番号で入力してください"
                disabled={disabled}
              />
              <Typography sx={{ fontSize: '12px', color: gray60, marginTop: '4px' }}>
                {itemValidationHelperText.patternMessage}
              </Typography>
            </Box>
          )}
        </>
      )}

      {showDateRange && (
        <Box sx={{ marginTop: '12px' }}>
          <Typography sx={{ fontSize: '14px', color: gray80, fontWeight: 'bold' }}>
            {itemValidationLabel.dateRangeSection}
          </Typography>
          {/* 基準日は閲覧日ではなく OCR を実行した日。後から開いても判定が動かない */}
          <Typography sx={{ fontSize: '12px', color: gray60, marginBottom: '4px' }}>
            {itemValidationHelperText.dateRange}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={dateNoPast}
                onChange={(e): void => onDateNoPastChange(e.target.checked)}
                disabled={disabled}
                sx={{ '&.Mui-checked': { color: CHECKBOX_COLOR } }}
              />
            }
            label={
              <Typography sx={{ fontSize: '14px', color: gray80 }}>
                {itemValidationLabel.dateNoPast}
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={dateNoFuture}
                onChange={(e): void => onDateNoFutureChange(e.target.checked)}
                disabled={disabled}
                sx={{ '&.Mui-checked': { color: CHECKBOX_COLOR } }}
              />
            }
            label={
              <Typography sx={{ fontSize: '14px', color: gray80 }}>
                {itemValidationLabel.dateNoFuture}
              </Typography>
            }
          />

          {/* 過去を一切許可しないなら、さかのぼれる上限を指定しても意味がないため非活性にする */}
          <Box sx={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
            <CustomTextfield
              labelText={itemValidationLabel.datePastLimit}
              value={dateNoPast ? '' : datePastLimit}
              onChange={(e): void => onDatePastLimitChange(e.target.value.replace(/[^0-9]/g, ''))}
              inputWidth="100%"
              height="56px"
              placeholder="例: 1"
              disabled={disabled || dateNoPast}
            />
            <CustomSelectField
              labelText="単位"
              value={datePastUnit}
              onChange={(e): void => onDatePastUnitChange(e.target.value as DateRangeUnit)}
              list={dateRangeUnitOptions.map(opt => ({ value: opt.value, option: opt.option }))}
              disabled={disabled || dateNoPast}
              height="56px"
              width="100%"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: '16px' }}>
            <CustomTextfield
              labelText={itemValidationLabel.dateFutureLimit}
              value={dateNoFuture ? '' : dateFutureLimit}
              onChange={(e): void => onDateFutureLimitChange(e.target.value.replace(/[^0-9]/g, ''))}
              inputWidth="100%"
              height="56px"
              placeholder="例: 1"
              disabled={disabled || dateNoFuture}
            />
            <CustomSelectField
              labelText="単位"
              value={dateFutureUnit}
              onChange={(e): void => onDateFutureUnitChange(e.target.value as DateRangeUnit)}
              list={dateRangeUnitOptions.map(opt => ({ value: opt.value, option: opt.option }))}
              disabled={disabled || dateNoFuture}
              height="56px"
              width="100%"
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default AdvancedValidationSettings;
